import { captureException } from "@sentry/vue";

export class PollingManager {
  constructor(apiUrl, itemId, updateCallback, messageHandler, responseHandler) {
    this.apiUrl = apiUrl;
    this.itemId = itemId;
    this.updateCallback = updateCallback;
    this.messageHandler = messageHandler;
    this.processedItems = new Set();
    this.messageQueue = [];
    this.isProcessingQueue = false;
    this.pollInterval = null;
    this.processQueueInterval = null;
    this.pollingStopped = false;
    this.currentRequestId = 0;
    this.responseHandler = responseHandler;
    this.boundMessageListener = null;
    this.attempts = 0;
  }

  async startPolling() {
    console.log("Started polling every 3s");
    await this.poll();
    this.pollInterval = setInterval(async () => {
      await this.poll();
    }, 3000);
    await this.startProcessingQueue();
  }

  async startProcessingQueue() {
    console.log("Started processing queue every 1s");
    await this.processQueue();
    this.processQueueInterval = setInterval(async () => {
      await this.processQueue();
    }, 1000);
  }

  async poll() {
    this.attempts++;
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        body: JSON.stringify({"item_id": this.itemId})
      });
      const data = await response.json();
      const items = JSON.parse(data.body).slides;
      if (items.length > 0) {
        this.processItems(items);
        if (items[items.length - 1].done) {
          this.stopPolling();
          return;
        }
      } else {
        this.stopPolling();
        return;
      }
    } catch (error) {
      console.error('Polling error:', error);
      captureException(error, { level: "error" });
    } finally {
      if (this.attempts > 100) {
        this.stopPolling();
        return;
      }
    }
  }

  processItems(items) {
    const processableItems = items.slice(1, -1);

    processableItems.forEach((item) => {
      if (item.status === 'succeeded' && !this.processedItems.has(item.slide_number)) {
        console.log(`Pushed slide number ${item.slide_number} to the queue`);
        this.processedItems.add(item.slide_number);
        this.enqueueMessage(item, item.slide_number);
      }
    });
  }

  enqueueMessage(item, index) {
    this.messageQueue.push({ item, index });
  }

  async processQueue() {
    if (this.isProcessingQueue || this.messageQueue.length === 0) {
      if (this.isProcessingQueue) {
        console.log(`Can't process queue because it's already processing`);
      } else if (this.messageQueue.length === 0) {
        console.log(`Can't process queue because it's empty`);
      }
      return;
    }

    this.isProcessingQueue = true;
    const { item, index } = this.messageQueue[0];
    console.log(`Processing slide number ${item.slide_number} currently`);
    const requestId = this.currentRequestId++;

    try {
      // Use the message handler callback
      let lastInsertedSlideName = await this.messageHandler(item, requestId);
      let recoveryAttempts = 0;
      while (!lastInsertedSlideName && recoveryAttempts < 10) {
        recoveryAttempts += 1;
        lastInsertedSlideName = await this.messageHandler(item, requestId, true);
      }
      console.log(lastInsertedSlideName);
      this.updateCallback(index, item, lastInsertedSlideName);
    } catch (error) {
      console.error('Message processing error:', error);
      captureException(error, { level: "error" });
    } finally {
      this.messageQueue.shift();
      this.isProcessingQueue = false;
      console.log("Checking whether we should stop processing queue");
      if (this.pollingStopped && this.messageQueue.length === 0) {
        this.stopProcessingQueue();
      }
    }
  }

  stopPolling() {
    clearInterval(this.pollInterval);
    console.log("Stopped polling");
    this.pollingStopped = true;
    if (!this.isProcessingQueue && this.messageQueue.length === 0) {
      this.stopProcessingQueue();
    }
  }

  stopProcessingQueue() {
    clearInterval(this.processQueueInterval);
    console.log("Stopped processing queue");
  }
}
